import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn, signUp } from '../lib/supabase'
import { resetPassword } from '../lib/supabase'
import './Auth.css'


const INTERESTS = ['Philosophy', 'Economics', 'Politics', 'Science', 'Culture', 'Technology', 'History', 'Ethics']

export default function Auth() {
  const navigate = useNavigate()
  const [mode, setMode] = useState('login') // 'login' | 'signup' | 'profile'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Login fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Signup fields
  const [signupEmail, setSignupEmail] = useState('')
  const [signupPassword, setSignupPassword] = useState('')
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [bio, setBio] = useState('')
  const [selectedInterests, setSelectedInterests] = useState([])
  const [signupData, setSignupData] = useState(null)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await signIn(email, password)
      navigate('/feed')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    if (!username.trim()) {
      setError('Please choose a pseudonym')
      return
    }
    if (username.includes(' ')) {
      setError('Username cannot contain spaces')
      return
    }
    setLoading(true)
    setError('')
    try {
      // Check if username already exists
      const { supabase } = await import('../lib/supabase')
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username.toLowerCase())
        .maybeSingle()

      if (existing) {
        setError('This username is already taken. Please choose a different one.')
        setLoading(false)
        return
      }

      const data = await signUp(
        signupEmail,
        signupPassword,
        username.toLowerCase(),
        (displayName || username).trim()
      )
      setSignupData(data)
      setMode('profile')
    } catch (err) {
      // Check for duplicate email error
      if (err.message && (err.message.includes('already registered') || err.message.includes('User already exists'))) {
        setError('This email is already registered. Please sign in or use a different email.')
      } else {
        setError(err.message || 'Failed to create account')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleProfileSetup = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      // Update profile with bio and interests
      const { supabase } = await import('../lib/supabase')
      if (signupData?.user) {
        await supabase.from('profiles').update({
          bio,
          interests: selectedInterests,
          avatar_color: getRandomColor()
        }).eq('id', signupData.user.id)
      }
      navigate('/feed')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleInterest = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    )
  }

  const getRandomColor = () => {
    const colors = ['#8a7048', '#3a5a7a', '#5a7a3a', '#7a3a5a', '#3a7a6a']
    return colors[Math.floor(Math.random() * colors.length)]
  }

  return (
    <div className="auth-page">
      <div className="auth-bg-pattern" />

      <div className="auth-container">
        {/* Brand */}
        <div className="auth-brand">
          <h1 className="auth-logo">Agora</h1>
          <p className="auth-tagline">the digital town square.</p>
        </div>

        {/* Login */}
        {mode === 'login' && (
          <div className="auth-card animate-slideUp">
            <div className="auth-card-header">
              <span className="mono" style={{ color: 'var(--text-dim)' }}>— Sign in</span>
            </div>
            <form onSubmit={handleLogin}>
              <div className="field">
                <label className="field-label">Email</label>
                <input
                  type="email"
                  className="field-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="field">
                <label className="field-label">Password</label>
                <input
                  type="password"
                  className="field-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Enter →'}
              </button>
              <button
  type="button"
  className="auth-link"
  style={{ marginTop: 10 }}
  onClick={async () => {
    const email = prompt("Enter your email to reset password:");
    if (!email) return;

    try {
      await resetPassword(email);
      alert("Password reset email sent!");
    } catch (e) {
      alert(e.message);
    }
  }}
>
  Forgot password?
</button>
            </form>
            <div className="auth-switch">
              No account?{' '}
              <button className="auth-link" onClick={() => { setMode('signup'); setError('') }}>
                Join Agora
              </button>
            </div>
          </div>
        )}

        {/* Signup */}
        {mode === 'signup' && (
          <div className="auth-card animate-slideUp">
            <div className="auth-card-header">
              <span className="mono" style={{ color: 'var(--text-dim)' }}>— Create account</span>
            </div>
            <form onSubmit={handleSignup}>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Display name <span style={{ color: 'var(--text-dim)' }}>(optional)</span></label>
                  <input
                    type="text"
                    className="field-input"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Persistent pseudonym</label>
                  <input
                    type="text"
                    className="field-input"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="@your-handle"
                    required
                  />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Email</label>
                <input
                  type="email"
                  className="field-input"
                  value={signupEmail}
                  onChange={e => setSignupEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="field">
                <label className="field-label">Password</label>
                <input
                  type="password"
                  className="field-input"
                  value={signupPassword}
                  onChange={e => setSignupPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  minLength={8}
                  required
                />
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Continue →'}
              </button>
            </form>
            <div className="auth-switch">
              Already a member?{' '}
              <button className="auth-link" onClick={() => { setMode('login'); setError('') }}>
                Sign in
              </button>
            </div>
          </div>
        )}

        {/* Profile setup */}
        {mode === 'profile' && (
          <div className="auth-card animate-slideUp">
            <div className="auth-card-header">
              <span className="mono" style={{ color: 'var(--accent)' }}>— Set up your profile</span>
            </div>
            <p className="auth-subtitle">
              Tell us what you think about. This shapes your feed — not your behavior.
            </p>
            <form onSubmit={handleProfileSetup}>
              <div className="field">
                <label className="field-label">Bio <span style={{ color: 'var(--text-dim)' }}>(optional)</span></label>
                <textarea
                  className="field-input field-textarea"
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  placeholder="What do you think about? What's your intellectual background?"
                  rows={3}
                  maxLength={280}
                />
              </div>
              <div className="field">
                <label className="field-label">Your interests</label>
                <div className="interests-grid">
                  {INTERESTS.map(interest => (
                    <button
                      key={interest}
                      type="button"
                      className={`interest-btn ${selectedInterests.includes(interest) ? 'selected' : ''}`}
                      onClick={() => toggleInterest(interest)}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
                <div className="mono" style={{ color: 'var(--text-dim)', marginTop: '0.5rem' }}>
                  {selectedInterests.length} selected — these seed your depth feed
                </div>
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Enter Agora →'}
              </button>
            </form>
          </div>
        )}

        {/* Philosophy note */}
        <div className="auth-philosophy">
          <div className="auth-philosophy-rule" />
          <p>
            Every capsule incubates for 30 minutes before publishing.<br />
            Responses incubate for 20 minutes.<br />
            You may withdraw or edit it anytime before then.<br />
            <strong>Persistent pseudonym, durable reputation.</strong>
          </p>
        </div>
      </div>
    </div>
  )
}
